import moment from 'moment'

/** Значения opz_status из front_get_same_day_rec_report. */
export type OpzStatus = 'success' | 'future' | 'cancel' | null | undefined

export interface Opz2Account {
    userId: string
    name: string | null
    email: string | null
}

/** Администратор yClients, создавший запись. */
export interface Opz2CrmAdmin {
    name: string | null
    email: string | null
}

export interface Opz2Dictionaries {
    staff: Record<string, string>
    clients: Record<string, { name: string | null; hrefInfo: any }>
    accounts: Record<string, Opz2Account>
    crmAdmins: Record<string, Opz2CrmAdmin>
}

export const EMPTY_DICTIONARIES: Opz2Dictionaries = {
    staff: {},
    clients: {},
    accounts: {},
    crmAdmins: {},
}

const STATUS_LABELS: Record<string, { text: string; tag: 'go' | 'wait' | 'cancel' }> = {
    success: { text: 'Завершён', tag: 'go' },
    future: { text: 'Запись на будущее', tag: 'wait' },
    cancel: { text: 'Отмена', tag: 'cancel' },
}

export function statusLabel(status: OpzStatus): string {
    return status ? STATUS_LABELS[status]?.text ?? String(status) : '—'
}

export function statusTag(status: OpzStatus): string {
    return status ? STATUS_LABELS[status]?.tag ?? 'wait' : 'wait'
}

export function formatDate(value?: string | null): string {
    if (!value) return '—'
    const parsed = moment(value)
    return parsed.isValid() ? parsed.format('DD.MM.YYYY') : '—'
}

export function formatNumber(value?: number | null): string {
    return new Intl.NumberFormat('ru-RU').format(Number(value ?? 0))
}

export function formatPercent(value?: number | null): string {
    return `${new Intl.NumberFormat('ru-RU').format(Number(value ?? 0))}%`
}

export function staffName(dictionaries: Opz2Dictionaries, staffId?: string | null): string {
    if (!staffId) return '—'
    return dictionaries.staff[staffId] ?? staffId
}

export function clientName(dictionaries: Opz2Dictionaries, clientId?: string | null): string {
    if (!clientId) return '—'
    return dictionaries.clients[clientId]?.name ?? clientId
}

/**
 * Колонка «Аккаунт» — email пользователя DH, сопоставленного с администратором
 * YClients в блоке 4 «Настройки KPI».
 *
 * Если сопоставления ещё нет, показываем id администратора YClients: иначе все
 * строки выглядят одинаковым прочерком и непонятно, кого сопоставлять.
 * Прочерк остаётся только там, где создателя записи нет вовсе.
 */
export function accountLabel(
    dictionaries: Opz2Dictionaries,
    creatingUserId?: string | null
): string {
    if (!creatingUserId) return '—'
    const account = dictionaries.accounts[creatingUserId]
    return account?.email ?? account?.name ?? `ID ${creatingUserId}`
}

/**
 * Колонка «Аккаунт» в списках записей — «Кросс-продажи» и «Детализация»
 * вкладки «ОПЗ 2.0» — это имя администратора из yClients
 * (решение функционального заказчика). Сопоставление с пользователем DH здесь
 * не нужно: показываем того, кто фактически создал запись.
 *
 * Если имени нет, откатываемся на почту админа, затем на сопоставленного
 * пользователя DH и только потом на id — чтобы строка не превращалась в прочерк.
 */
export function crmAdminLabel(
    dictionaries: Opz2Dictionaries,
    creatingUserId?: string | null
): string {
    if (!creatingUserId) return '—'
    const admin = dictionaries.crmAdmins?.[creatingUserId]
    return admin?.name ?? admin?.email ?? accountLabel(dictionaries, creatingUserId)
}

/**
 * Колонка «Аккаунт» в KPI: строки приходят из функции как есть, в том числе по
 * администраторам без сопоставления с DH. Сопоставленным — пользователь DH,
 * остальным — имя из yClients; служебные значения функции («Нет данных»)
 * выводим как есть.
 */
export function kpiAccountLabel(
    dictionaries: Opz2Dictionaries,
    creatingUserId?: string | null
): string {
    if (!creatingUserId) return '—'
    if (dictionaries.accounts[creatingUserId]) return accountLabel(dictionaries, creatingUserId)
    if (!/^\d+$/.test(creatingUserId)) return creatingUserId
    return crmAdminLabel(dictionaries, creatingUserId)
}

export type CrossChip = 'all' | 'same_day' | 'future_day' | 'firstly'

export interface CrossSailRow {
    visitDate?: string | null
    opzVisitDate?: string | null
    opzIsFirstly?: boolean | null
}

/**
 * Чипы вкладки «ОПЗ к другому мастеру/услуге» фильтруют уже загруженные строки
 * на клиенте, без запроса к серверу (US5S3).
 */
export function matchesChip(row: CrossSailRow, chip: CrossChip): boolean {
    if (chip === 'same_day') {
        return Boolean(row.visitDate) && row.visitDate === row.opzVisitDate
    }
    if (chip === 'future_day') {
        return Boolean(row.visitDate && row.opzVisitDate) && row.opzVisitDate! > row.visitDate!
    }
    if (chip === 'firstly') {
        return Boolean(row.opzIsFirstly)
    }
    return true
}

/** «Всего ОПЗ» = ОПЗ к тому же мастеру + ОПЗ к другому мастеру/услуге (US6S2). */
export function totalOpz(same?: number | null, other?: number | null): number {
    return Number(same ?? 0) + Number(other ?? 0)
}

/** Отмены в таблице KPI суммируются по обоим типам ОПЗ. */
export function totalCancels(sameCancel?: number | null, otherCancel?: number | null): number {
    return Number(sameCancel ?? 0) + Number(otherCancel ?? 0)
}

/** Доля в процентах с одним знаком, без деления на ноль. */
export function share(part?: number | null, whole?: number | null): number {
    const total = Number(whole ?? 0)
    if (!total) return 0
    return Math.round((Number(part ?? 0) / total) * 1000) / 10
}

/** Ключ кэша данных вкладки: перезапрашиваем только при смене фильтров. */
export function filtersKey(filters: Record<string, any>): string {
    return JSON.stringify(filters ?? {})
}
